import DisplaySettingsOutlined from "@mui/icons-material/DisplaySettingsOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import PaidOutlined from "@mui/icons-material/PaidOutlined";
import PersonOutline from "@mui/icons-material/PersonOutline";
import StoreOutlined from "@mui/icons-material/StoreOutlined";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import { useEffect, useMemo, useState } from "react";
import { useFormState } from "react-final-form";
import ToggleIconButton from "../../_src_shared/buttons/ToggleIconButton";
import RffAddressGeolocateField from "../../components/finalForm/inputs/RffAddressGeolocateField";
import RffBooleanCheckbox from "../../components/finalForm/inputs/RffBooleanCheckbox";
import RffBooleanToggleField from "../../components/finalForm/inputs/RffBooleanToggleField";
import RffDateField from "../../components/finalForm/inputs/RffDateField";
import RffDollarField from "../../components/finalForm/inputs/RffDollarField";
import RffFloatField from "../../components/finalForm/inputs/RffFloatField";
import RffNumberField from "../../components/finalForm/inputs/RffNumberField";
import RffPercentField from "../../components/finalForm/inputs/RffPercentField";
import RffSelectField from "../../components/finalForm/inputs/RffSelectField";
import RffTextareaField from "../../components/finalForm/inputs/RffTextareaField";
import RffForm from "../../components/finalForm/RffForm";
import RffGroup from "../../components/finalForm/shared/RffGroup";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import TitledGroup from "../../components/ui/TitledGroup";
import { selectOptionsEntityType } from "../../constants/selectOptions/selectOptionsEntityType";
import { useLoanSizerHook } from "../../hooks/useLoanSizerHook";

const LoanIntakePricingSizerForm = ({
  setResults,

  isHandsomeHomebuyer,
  formSpy,
}) => {
  const {
    loading,
    createRtlSizerPricing,
    createPartnerSizerPricingHandHomebuyer,
  } = useLoanSizerHook();

  const [collapsed, setCollapsed] = useState(false);
  const onFormSubmit = (formData) => {
    let data = { ...formData };

    if (isHandsomeHomebuyer) {
      const updatedData = { ...data };
      updatedData.brokerOriginationPoints = data.partnerOriginationPoints;
      createPartnerSizerPricingHandHomebuyer({
        data: updatedData,
        onSuccessFn: (d) => {
          console.log("Results", d);
          setCollapsed(true);
          setResults(d);
        },
      });
      return;
    }
    createRtlSizerPricing({
      data: data,
      onSuccessFn: (d) => {
        console.log("Results", d);
        setResults(d);
      },
    });
  };

  const initialValues = useMemo(() => {
    const defaultState = {
      propertyRuralIndicator: false,
      floodZone: false,
      sizerForeignNationalFlag: false,
      sizerBorrowers: {
        sizerEnteredExperience: {
          fix_and_flip_sold: 0,
          fix_and_flip_retained_rental: 0,
          income_producing_properties_purchased: 0,
          ground_up_completed_and_sold: 0,
          ground_up_retained_as_rental: 0,
          gc_for_third_party: 0,
        },
      },
    };

    return defaultState;
  }, []);

  return (
    <Card
      sx={{
        maxWidth: "880px",
        flexShrink: 1,
        minWidth: "320px",

        p: 3,
      }}
    >
      <RffForm
        formSpy={formSpy}
        loading={loading}
        submitText="Calculate & Price"
        maxWidth={false}
        initialValues={initialValues}
        onSubmit={onFormSubmit}
        formTitle={"Create Loan"}
        formDescription="Please provide as much information as possible regarding your loan, borrowers, and properties."
        finalStepLabel="Submit"
      >
        <Flx column gap={4} sx={{ mb: 2 }}>
          <LoanDetailQuestions collapsed={collapsed} />
          <Divider flexItem />
          <BorrowerAndGuarantorQuestions collapsed={collapsed} />
          <Divider flexItem />
          <PropertyQuestions collapsed={collapsed} />
          <Divider flexItem />
          <LoanPricingQuestions
            collapsed={collapsed}
            isHandsomeHomebuyer={isHandsomeHomebuyer}
          />
        </Flx>
      </RffForm>
    </Card>
  );
};

const LoanDetailQuestions = ({ collapsed }) => {
  const product_options = useMemo(
    () => ["Fix and Flip", "Stabilized Bridge"],
    []
  );
  const loan_purpose_options = useMemo(
    () => [
      "Purchase",
      "Refinance - No Cash Out",
      "Refinance - Cash Out",
      "Delayed Purchase",
    ],
    []
  );
  return (
    <GroupCard
      icon={<DisplaySettingsOutlined />}
      title="Loan Details"
      collapsed={collapsed}
    >
      <RffSelectField
        size={6}
        name="loanProductType"
        label="Loan Product Type"
        required
        options={product_options}
      />

      <RffSelectField
        size={6}
        name="loanPurpose"
        label="Loan Purpose"
        required
        options={loan_purpose_options}
      />
      <RffTextareaField
        minHeight={80}
        name="exitStrategy"
        minCharCount={10}
        helperText={"Minimum 10 characters"}
        label="Exit Strategy"
      />
    </GroupCard>
  );
};

const BorrowerAndGuarantorQuestions = ({ collapsed }) => {
  return (
    <GroupCard
      collapsed={collapsed}
      icon={<PersonOutline />}
      titleType="h3"
      suppressBottomMargin
      title="Borrower & Guarantor"
    >
      <RffNumberField
        size={6}
        required
        name="sizerBorrowers.sizerGuarantorCount"
        label="Number of Guarantors"
      />
      <RffSelectField
        required
        name="sizerBorrowers.sizerEntityType"
        label="Borrower Entity Type"
        size={6}
        options={selectOptionsEntityType}
      />

      <RffDollarField
        size={6}
        required
        name="sizerBorrowers.allCashLiquidity"
        label="Cash Liquidity (All Borrowers)"
      />
      <RffDollarField
        size={6}
        required
        name="sizerBorrowers.allOtherLiquidity"
        label="Other Liquidity (All Borrowers)"
      />
      <RffNumberField
        size={6}
        required
        name="sizerBorrowers.sizerCreditScore"
        label="Best FICO (All Borrowers)"
        min={0}
        helperText="Credit score must be between 0 and 850"
        max={850}
      />
      <RffBooleanToggleField
        size={6}
        required
        name="sizerBorrowers.sizerForeignNationalFlag"
        label="Is the borrower a foreign national?"
      />
      {/* ------------------------- */}

      {/* sizerBorrowers.sizerEnteredExperience  */}
      <RffGroup sx={{ mt: 2 }} isGridChild suppressBottomMargin rowSpacing={2}>
        <RffGroup disableGrid suppressBottomMargin isGridChild rowSpacing={0}>
          <Flx column>
            <Txt bold>Experience</Txt>
            <Txt secondary>
              For underwriting purposes, qualifying experience should be within
              the last three years
            </Txt>
          </Flx>
        </RffGroup>
        <RffNumberField
          required
          size={6}
          name="sizerBorrowers.sizerEnteredExperience.fix_and_flip_sold"
          label="Fix & flip sold"
        />

        <RffNumberField
          required
          size={6}
          name="sizerBorrowers.sizerEnteredExperience.fix_and_flip_retained_rental"
          label="Fix & flip retained as rental"
        />

        <RffNumberField
          required
          size={6}
          name="sizerBorrowers.sizerEnteredExperience.income_producing_properties_purchased"
          label="Income-producing properties purchased"
        />

        <RffNumberField
          required
          size={6}
          name="sizerBorrowers.sizerEnteredExperience.ground_up_completed_and_sold"
          label="Ground-up completed and sold"
        />

        <RffNumberField
          required
          size={6}
          name="sizerBorrowers.sizerEnteredExperience.ground_up_retained_as_rental"
          label="Ground-up retained as rental"
        />

        <RffNumberField
          required
          size={6}
          name="sizerBorrowers.sizerEnteredExperience.gc_for_third_party"
          label="GC for third party"
        />
      </RffGroup>

      {/* sizerBorrowers.sizerBackgroundFlags  */}

      <RffGroup sx={{ mt: 2 }} suppressBottomMargin isGridChild rowSpacing={1}>
        <RffGroup disableGrid suppressBottomMargin isGridChild rowSpacing={0}>
          <Txt bold>Background Flags</Txt>
        </RffGroup>
        <RffGroup
          sx={{ ml: 1 }}
          suppressBottomMargin
          isGridChild
          rowSpacing={0}
        >
          <RffBooleanCheckbox
            name="sizerBorrowers.sizerBackgroundFlags.declared_bankruptcy_4yrs"
            label="Bankruptcy in last 4 years"
          />
          <RffBooleanCheckbox
            name="sizerBorrowers.sizerBackgroundFlags.bankruptcy_2yrs"
            label="Bankruptcy in last 2 years"
          />
          <RffBooleanCheckbox
            name="sizerBorrowers.sizerBackgroundFlags.foreclosure_2yrs"
            label="Foreclosure in last 2 years"
          />
          <RffBooleanCheckbox
            name="sizerBorrowers.sizerBackgroundFlags.short_sale_deed_in_lieu_2yrs"
            label="Short sale or deed in lieu in last 2 years"
          />
          <RffBooleanCheckbox
            name="sizerBorrowers.sizerBackgroundFlags.judgments_liens_gt_15k"
            label="Judgments or liens > $15,000"
          />
          <RffBooleanCheckbox
            name="sizerBorrowers.sizerBackgroundFlags.ofac_derogatory"
            label="Derogatory OFAC findings"
          />
          <RffBooleanCheckbox
            name="sizerBorrowers.sizerBackgroundFlags.mortgage_30day_late_12mo"
            label="30-day late on mortgage in past year"
          />
          <RffBooleanCheckbox
            name="sizerBorrowers.sizerBackgroundFlags.felony"
            label="Felony conviction"
          />
          <RffBooleanCheckbox
            name="sizerBorrowers.sizerBackgroundFlags.fraud"
            label="Convicted of any crime involving fraud, including misdemeanors"
          />
        </RffGroup>
      </RffGroup>
    </GroupCard>
  );
};

const PropertyQuestions = ({ collapsed }) => {
  const { values } = useFormState();

  const isFixAndFlip = values?.loanProductType === "Fix and Flip";
  const isBridge = values?.loanProductType === "Stabilized Bridge";
  const isRefi =
    values?.loanPurpose === "Refinance - No Cash Out" ||
    values?.loanPurpose === "Refinance - Cash Out";

  const isPurchase =
    values?.loanPurpose === "Purchase" ||
    values?.loanPurpose === "Delayed Purchase";
  /**
   * Options
   */
  const rtl_property_types = useMemo(
    () => [
      "SFR",
      "2-unit",
      "3-unit",
      "4-unit",
      "Condo",
      "Townhome",
      "Planned Unit Development (PUD)",
      "Non-Warrantable Condo",
    ],
    []
  );

  const mappedPropertyConditionAtOriginationOptions = useMemo(() => {
    const propertyConditionAtOriginationOptions = [
      "C1",
      "C2",
      "C3",
      "C4",
      "C5",
      "C6",
      "TBD",
    ];
    const descriptions = {
      C1: "New construction or completely renovated",
      C2: "Minor repairs needed, no major issues",
      C3: "Average condition, some repairs needed",
      C4: "Below average condition, significant repairs needed",
      C5: "Poor condition, extensive repairs needed",
      C6: "Requires major renovations",
      TBD: "To be determined",
    };
    return propertyConditionAtOriginationOptions?.map((o) => {
      return {
        label: `${o}: ${descriptions[o]}`,
        optionLabel: `${o}: ${descriptions[o]}`,
        value: o,
      };
    });
  }, []);

  return (
    <GroupCard
      icon={<StoreOutlined />}
      title="Subject Property"
      collapsed={collapsed}
    >
      <RffAddressGeolocateField
        name="subjectProperty.address"
        required
        label="Property Address"
      />
      <RffDollarField
        name="subjectProperty.asIsValue"
        required
        label="As-Is Value"
        size={4}
      />
      <RffDollarField
        size={4}
        name="subjectProperty.purchasePrice"
        required
        label={isRefi ? "Original Purchase Price" : "Purchase Price"}
      />
      <RffDateField
        size={4}
        name="subjectProperty.purchaseDate"
        required
        label={isRefi ? "Original Purchase Date" : "Purchase Date"}
      />
      <RffSelectField
        size={5}
        name="subjectProperty.propertyType"
        required
        label="Property Type"
        options={rtl_property_types}
      />

      <RffSelectField
        size={7}
        name="subjectProperty.propertyConditionAtOrigination"
        required
        label="Property Condition at Origination"
        options={mappedPropertyConditionAtOriginationOptions}
      />

      {isFixAndFlip && (
        <>
          <RffDollarField
            name="subjectProperty.constructionBudget"
            required
            size={6}
            label="Construction Budget"
          />
          <RffDollarField
            name="subjectProperty.arv"
            required
            size={6}
            label="After Repair Value (ARV)"
          />
        </>
      )}

      {isRefi && (
        <RffDollarField
          name="subjectProperty.additionalBasis"
          required
          label="Additional Basis"
        />
      )}
      {isPurchase && (
        <>
          <RffDollarField
            size={6}
            name="subjectProperty.assignmentFees"
            required
            label="Assignment Fees"
          />
          <RffDollarField
            size={6}
            name="subjectProperty.sellersConcession"
            required
            label="Seller's Concession"
          />
        </>
      )}

      {isBridge && (
        <>
          <RffNumberField
            size={6}
            name="subjectProperty.vacantUnitCount"
            required
            label="Vacant Unit Count"
          />
          <RffDollarField
            size={6}
            name="subjectProperty.totalMonthlyRent"
            required
            label="Total Monthly Rent"
          />
          <RffDollarField
            size={6}
            name="subjectProperty.annualTaxes"
            required
            label="Annual Taxes"
          />
          <RffDollarField
            size={6}
            name="subjectProperty.annualInsurance"
            required
            label="Annual Insurance"
          />
          <RffDollarField
            size={6}
            name="subjectProperty.annualFloodInsurance"
            required
            label="Annual Flood Insurance"
          />
          <RffDollarField
            size={6}
            name="subjectProperty.annualHoaFees"
            required
            label="Annual HOA Fees"
          />
          <RffDollarField
            size={6}
            name="subjectProperty.annualPropertyMgmtFees"
            required
            label="Annual Property Management Fees"
          />
          <RffDollarField
            size={6}
            name="subjectProperty.annualOtherExpense"
            required
            label="Annual Other Expense"
          />
        </>
      )}
      <RffBooleanToggleField
        name="subjectProperty.propertyRuralIndicator"
        required
        label="Is a rural property"
        size={6}
      />
      <RffBooleanToggleField
        size={6}
        required
        name="subjectProperty.floodZone"
        label="Flood Zone"
      />
    </GroupCard>
  );
};

const LoanPricingQuestions = ({ isHandsomeHomebuyer, collapsed }) => {
  return (
    <GroupCard icon={<PaidOutlined />} title="Pricing">
      {isHandsomeHomebuyer ? (
        <>
          <RffPercentField
            size={6}
            // size={collapsed ? 12 : 6}
            required
            name="sizerInitialRequestedLeverage"
            label="Requested Initial Leverage"
          />
          <RffPercentField
            size={6}
            // size={collapsed ? 12 : 6}
            required
            name="sizerFinancedBudgetPercent"
            label="Financed Budget Percent"
          />
          <RffFloatField
            size={6}
            // size={collapsed ? 12 : 6}
            required
            name="partnerOriginationPoints"
            placeholder="Ex. 1.5 Points"
            label="Partner Origination Points"
            min={0}
            max={1.5}
          />
          <RffPercentField
            size={6}
            // size={collapsed ? 12 : 6}
            required
            name="requestedRate"
            // helperText="Max 1.5 Points"
            label="Requested Rate"
            min={0}
            max={1.5}
          />
        </>
      ) : (
        <>
          <RffPercentField
            size={4}
            required
            name="sizerInitialRequestedLeverage"
            label="Requested Initial Leverage"
          />
          <RffPercentField
            size={4}
            required
            name="sizerFinancedBudgetPercent"
            label="Financed Budget Percent"
          />
          <RffFloatField
            size={4}
            required
            name="brokerOriginationPoints"
            helperText="Max 1.5 Points"
            label="Broker Origination Points"
            min={0}
            max={1.5}
          />
        </>
      )}
    </GroupCard>
  );
};

const GroupCard = ({ children, title, icon, collapsed, sx }) => {
  const [show, setShow] = useState(true);

  useEffect(() => {
    setShow(!collapsed);
  }, [collapsed]);
  return (
    <TitledGroup
      headerBottomPadding={show ? 1 : 0}
      suppressHeaderPadding
      onHeaderClick={() => setShow((s) => !s)}
      headerEndContent={
        <ToggleIconButton size="small" onClick={setShow}>
          {show ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </ToggleIconButton>
      }
      bodySx={{ py: show ? 2 : 0, px: 0 }}
      sx={{
        p: 0,
        mt: 1,

        ".titled-group-header-root": {
          color: "#000000",
          h3: {
            fontWeight: 600,
            lineHeight: 1,
          },
        },

        flexShrink: 1,
        flexGrow: 0,
        ...sx,
      }}
      icon={icon}
      title={title}
    >
      {show ? (
        <RffGroup
          rowSpacing={3.6}
          // columnSpacing={3.2}
          // rowSpacing={3}
          //
          suppressBottomMargin
          sx={{ width: "100%" }}
        >
          {children}
        </RffGroup>
      ) : null}
    </TitledGroup>
  );
};

export default LoanIntakePricingSizerForm;
